import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Buildings, Lightning, CreditCard, ArrowRight, Coins, CheckCircle, GraduationCap, Info } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const BranchSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [creditsData, setCreditsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchTraining, setBranchTraining] = useState('');
  const [savingTraining, setSavingTraining] = useState(false);

  useEffect(() => {
    fetchBranchData();
  }, []);

  const fetchBranchData = async () => {
    try {
      // Get user's assigned branch
      const userBranches = user?.assigned_branches || [];
      if (userBranches.length === 0) {
        toast.error('No branch assigned to your account');
        setLoading(false);
        return;
      }

      const branchId = userBranches[0];

      // Fetch branch details
      const branchResponse = await api.get(`/branches/${branchId}`);
      setBranch(branchResponse.data);

      // Fetch credits data
      const creditsResponse = await api.get(`/billing/credits/${branchId}`);
      setCreditsData(creditsResponse.data);

      // Fetch branch-specific AI training content
      try {
        const trainingResponse = await api.get(`/ai-settings/branch/${branchId}`);
        setBranchTraining(trainingResponse.data.content || '');
      } catch (err) {
        // No training content yet, that's fine
        setBranchTraining('');
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to load branch data:', error);
      toast.error('Failed to load branch settings');
      setLoading(false);
    }
  };

  const handleSaveTraining = async () => {
    if (!branch) return;
    
    setSavingTraining(true);
    try {
      await api.put(`/ai-settings/branch/${branch.branch_id}`, { 
        content: branchTraining 
      });
      toast.success('Branch AI training content updated successfully!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save training content'));
    } finally {
      setSavingTraining(false);
    }
  };

  const handleConfigureBilling = () => {
    if (branch) {
      navigate(`/branches/${branch.branch_id}/billing`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!branch) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-6">
          <Card className="p-12 text-center">
            <Buildings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Branch Assigned</h2>
            <p className="text-gray-500">Please contact your administrator to assign a branch to your account.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  const billingMethod = branch.ai_billing_method;
  const hasApiKey = Boolean(branch.ai_api_key);
  const creditsBalance = branch.ai_credits_balance || 0;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-light text-[#2C4C3B] mb-2">Branch Settings</h1>
          <p className="text-sm text-gray-600">{branch.name}</p>
        </div>

        {/* Branch Info Card */}
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#2C4C3B]/10 rounded-lg">
                <Buildings size={32} className="text-[#2C4C3B]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#2C4C3B] mb-1">{branch.name}</h2>
                {branch.address && <p className="text-sm text-gray-600 mb-1">📍 {branch.address}</p>}
                {branch.phone && <p className="text-sm text-gray-600 mb-1">📞 {branch.phone}</p>}
                {branch.email && <p className="text-sm text-gray-600">✉️ {branch.email}</p>}
              </div>
            </div>
            <Badge variant={branch.is_active ? "default" : "secondary"}>
              {branch.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </Card>

        {/* AI Billing Status Card - PROMINENT PLACEMENT */}
        <Card className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-[#2C4C3B]/20">
          <div className="flex items-center gap-3 mb-4">
            <Lightning size={32} className="text-yellow-500" weight="fill" />
            <div>
              <h2 className="text-2xl font-bold text-[#2C4C3B]">AI Features & Credits</h2>
              <p className="text-sm text-gray-600">Your branch's AI access configuration</p>
            </div>
          </div>

          {/* Current Status */}
          <div className="mb-6">
            <div className="text-sm text-gray-600 mb-2">Current AI Access Status:</div>
            {!billingMethod && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="font-semibold text-red-600">Not Configured</span>
                <span className="text-sm text-gray-500 ml-2">- AI features are currently disabled</span>
              </div>
            )}
            {billingMethod === 'own_key' && (
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-green-500" weight="fill" />
                <span className="font-semibold text-green-600">Own API Key</span>
                <span className="text-sm text-gray-500 ml-2">- Using custom API key</span>
              </div>
            )}
            {billingMethod === 'credits' && (
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-blue-500" weight="fill" />
                <span className="font-semibold text-blue-600">Credits Method</span>
                <Badge variant="outline" className="ml-2">
                  <Coins size={16} className="mr-1" />
                  {creditsBalance} credits
                </Badge>
              </div>
            )}
          </div>

          {/* Credits Dashboard (if using credits method) */}
          {billingMethod === 'credits' && (
            <Card className="p-4 mb-4 bg-white border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600">Available Credits</div>
                  <div className="text-3xl font-bold text-[#2C4C3B]">
                    {creditsBalance}
                    <span className="text-lg font-normal text-gray-600 ml-2">credits</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Each AI generation costs 1 credit (~₹1)
                  </div>
                </div>
                <Coins size={48} className="text-yellow-500" weight="fill" />
              </div>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {billingMethod && (
              <Button
                onClick={handleConfigureBilling}
                className="bg-[#2C4C3B] flex items-center gap-2 text-lg px-6 py-6 h-auto"
                data-testid="configure-billing-button"
                size="lg"
              >
                <CreditCard size={24} />
                <span className="font-semibold">
                  {billingMethod === 'credits' ? '💳 Recharge Credits Now' : '🔑 Manage API Key'}
                </span>
                <ArrowRight size={20} />
              </Button>
            )}
            
            {!billingMethod && (
              <div className="text-sm text-gray-600 bg-yellow-50 p-4 rounded-lg border-2 border-yellow-300">
                <strong className="text-base">⚠️ AI Features Not Configured</strong><br />
                <span className="text-gray-700">Please contact your administrator to configure AI billing for this branch.</span>
              </div>
            )}

            {creditsBalance > 0 && billingMethod === 'credits' && (
              <Badge variant="outline" className="flex items-center gap-1 px-3">
                <CheckCircle size={16} className="text-green-500" />
                AI Features Active
              </Badge>
            )}
          </div>
        </Card>

        {/* Branch-Specific AI Training Content */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <GraduationCap size={32} className="text-[#C05C42] flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-[#2C4C3B]">Branch-Specific AI Training</h2>
                <p className="text-sm text-[#1C1C1A]/60 mt-1">
                  Add custom instructions for AI guides specific to your branch
                </p>
              </div>
            </div>

            {/* Info Card */}
            <Card className="p-4 bg-[#FDF5E6] border-[#C05C42]/20">
              <div className="flex items-start gap-3">
                <Info size={20} className="text-[#C05C42] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[#1C1C1A]/80">
                  <p className="font-medium mb-1">How It Works:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>These instructions are <strong>added to</strong> the global training content</li>
                    <li>Use this to specify branch-specific products, services, or guidelines</li>
                    <li>Staff guides for your branch will follow both global and your branch instructions</li>
                  </ul>
                </div>
              </div>
            </Card>

            <div>
              <Label className="text-base font-medium text-[#2C4C3B]">
                Branch Training Content
              </Label>
              <p className="text-sm text-[#1C1C1A]/60 mt-1 mb-3">
                Add branch-specific instructions (optional)
              </p>
              <Textarea
                value={branchTraining}
                onChange={(e) => setBranchTraining(e.target.value)}
                placeholder="Example: At Vellore branch, we specialize in sports injury recovery. Emphasize our advanced Jade Bed with infrared settings. We have extended hours on weekends..."
                className="min-h-[250px] font-mono text-sm"
              />
              <p className="text-xs text-[#1C1C1A]/60 mt-2">
                Tip: Include branch specialties, unique offerings, local language preferences, or staff names
              </p>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-[#1C1C1A]/60">
                {branchTraining.length} characters
              </p>
              <Button
                onClick={handleSaveTraining}
                disabled={savingTraining}
                className="bg-[#2C4C3B] hover:bg-[#2C4C3B]/90"
              >
                {savingTraining ? 'Saving...' : 'Save Branch Training'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-4">
            <h4 className="font-semibold text-[#2C4C3B] mb-3 flex items-center gap-2">
              <Lightning size={20} />
              Available AI Features
            </h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• <strong>AI Assistant</strong>: Context-aware chatbot for customer queries</li>
              <li>• <strong>AI Morning Guides</strong>: Personalized session talking points</li>
              <li>• Billing applies per AI generation</li>
            </ul>
          </Card>

          <Card className="p-4">
            <h4 className="font-semibold text-[#2C4C3B] mb-3 flex items-center gap-2">
              <CreditCard size={20} />
              Billing Options
            </h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• <strong>Own API Key</strong>: Use your own Emergent LLM key</li>
              <li>• <strong>Credits</strong>: Flexible recharge (AI ₹1.50/credit, Notifications ₹15/credit)</li>
              <li>• Switch methods anytime</li>
            </ul>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default BranchSettings;
